import React, { useEffect, useState, createContext, useContext, useCallback } from "react";
import { toast } from "@/lib/toast";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Warning, X } from "@phosphor-icons/react";
import { fmtDate, fmtDateTime } from "@/lib/fmt";

const Ctx = createContext({ status: null, refresh: () => {}, toggle: () => {} });

export function useTrainingMode() { return useContext(Ctx); }

export function TrainingModeProvider({ children }) {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const canManage = user && ["owner", "administrator"].includes(user.role);

  const refresh = useCallback(async () => {
    if (!user) { setStatus(null); return; }
    try {
      // Admin/owner get full counts; everyone else gets the on/off signal only.
      const path = canManage ? "/admin/training-mode" : "/training-mode/status";
      const { data } = await api.get(path);
      setStatus(data);
    } catch { setStatus({ enabled: false }); }
  }, [user, canManage]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    if (!user) return;
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
  }, [user, refresh]);

  const toggle = async (enable) => {
    try {
      if (!enable && (status?.training_docs_total || 0) > 0) {
        const ok = window.confirm(
          `Turn OFF training mode?\n\nThis will PERMANENTLY DELETE ${status.training_docs_total} training document${status.training_docs_total === 1 ? "" : "s"}:\n\n${Object.entries(status.training_docs_by_collection || {}).map(([k, v]) => `  • ${v} ${k}`).join("\n")}\n\nProceed?`
        );
        if (!ok) return;
      }
      const { data } = await api.post("/admin/training-mode", { enabled: enable, confirm_purge: !enable });
      if (data.enabled) {
        toast.success("Training mode ON — all writes flagged as demo data");
      } else {
        toast.success(`Training mode OFF · ${data.purged_total || 0} demo records purged`);
      }
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Toggle failed");
    }
  };

  return <Ctx.Provider value={{ status, refresh, toggle, canManage }}>{children}</Ctx.Provider>;
}

export function TrainingBanner() {
  const { status, toggle, canManage } = useTrainingMode();
  if (!status?.enabled) return null;
  return (
    <div className="w-full bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white shadow-lg" data-testid="training-banner">
      <div className="max-w-[1400px] mx-auto px-4 py-2 flex items-center gap-3 flex-wrap">
        <Warning size={18} weight="fill" className="animate-pulse" />
        <div className="font-display font-black text-sm tracking-wide">TRAINING MODE ACTIVE</div>
        <div className="text-xs opacity-90">
          Every new record is flagged demo · {status.training_docs_total || 0} throwaway document{status.training_docs_total === 1 ? "" : "s"} so far · Started {status.started_at ? fmtDateTime(status.started_at) : "—"}
        </div>
        {canManage && (
          <button onClick={() => toggle(false)} data-testid="training-banner-off"
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-white/20 hover:bg-white/30 text-xs font-bold">
            <X size={12} weight="bold" /> Turn off & purge
          </button>
        )}
      </div>
    </div>
  );
}

export function TrainingModeToggleCard() {
  const { status, toggle, canManage, refresh } = useTrainingMode();
  const [busy, setBusy] = useState("");
  const [creds, setCreds] = useState(null);
  useEffect(() => { refresh(); }, [refresh]);
  if (!canManage) return null;
  const on = !!status?.enabled;

  const provision = async () => {
    setBusy("provision");
    try {
      const { data } = await api.post("/admin/training/provision-trainees");
      setCreds([...data.provisioned, ...data.existed_and_reset]);
      toast.success(`${data.provisioned.length + data.existed_and_reset.length} trainee accounts ready`);
      await refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(""); }
  };

  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-5" data-testid="training-mode-card">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Warning size={16} weight="fill" className={on ? "text-amber-500" : "text-neutral-400"} />
            <div className="font-display font-black tracking-wide">Training / Demo Mode</div>
            {on && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500 text-white">ACTIVE</span>}
          </div>
          <p className="text-xs text-neutral-500 max-w-md">
            Turn ON to train dispatchers or freight agents. Every load, envelope, invoice, chat message, and notification they create is flagged as demo data. All external emails still send (you asked for 100% realism). When you turn OFF, demo data is permanently purged.
          </p>
          {on && status?.training_docs_total > 0 && (
            <div className="mt-3 text-xs text-neutral-600 bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="training-mode-summary">
              <strong className="text-amber-800">Currently {status.training_docs_total} training document{status.training_docs_total === 1 ? "" : "s"}:</strong>
              <div className="mt-1 flex flex-wrap gap-1">
                {Object.entries(status.training_docs_by_collection || {}).map(([k, v]) => (
                  <span key={k} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white text-amber-900 border border-amber-200 font-mono text-[11px]">
                    {v} × {k}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        <button
          onClick={() => toggle(!on)}
          data-testid="training-mode-toggle"
          className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors shrink-0 ${on ? "bg-amber-500" : "bg-neutral-300"}`}
          aria-label="Toggle training mode"
        >
          <span className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform ${on ? "translate-x-9" : "translate-x-1"}`} />
        </button>
      </div>

      {on && (
        <div className="mt-5 pt-5 border-t border-neutral-100 space-y-4" data-testid="training-actions">
          <TraineeScorecard />
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-600">Sandbox trainees</div>
              <button onClick={provision} disabled={busy === "provision"} data-testid="provision-trainees"
                className="ml-auto text-xs font-semibold px-3 py-1.5 rounded-md bg-neutral-900 text-white hover:bg-black disabled:opacity-50">
                {busy === "provision" ? "Creating…" : (creds ? "Reset passwords" : "Provision trainees")}
              </button>
            </div>
            {creds ? (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 space-y-1.5" data-testid="trainee-creds">
                <div className="text-[10px] uppercase font-bold tracking-widest text-emerald-800">Hand these to your trainees</div>
                {creds.map((c) => (
                  <div key={c.email} className="flex items-center gap-2 text-xs bg-white rounded px-2.5 py-1.5 font-mono">
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-neutral-900 text-white uppercase">{c.role.replace("_", " ")}</span>
                    <span className="text-neutral-700">{c.email}</span>
                    <span className="ml-auto text-neutral-500">·</span>
                    <span className="font-bold text-neutral-900">{c.password}</span>
                  </div>
                ))}
                <div className="text-[10px] text-emerald-700 mt-1">These accounts + everything they create purge when you turn training off.</div>
              </div>
            ) : (
              <p className="text-xs text-neutral-500">Creates two throwaway accounts (dispatcher + freight agent) with known passwords. Purged automatically on toggle-off.</p>
            )}
          </div>

          <ScenariosBlock busy={busy} setBusy={setBusy} onChange={refresh} />
        </div>
      )}
    </div>
  );
}

function ScenarioTile({ title, desc, scenario, busy, onSeed }) {
  const key = `seed-${scenario}`;
  return (
    <button onClick={() => onSeed(scenario)} disabled={busy === key} data-testid={`seed-${scenario}`}
      className="text-left rounded-lg border border-neutral-200 bg-neutral-50 hover:bg-white hover:border-amber-300 transition p-3 disabled:opacity-60">
      <div className="font-display font-black text-sm text-neutral-900">{title}</div>
      <div className="text-[11px] text-neutral-500 mt-0.5 leading-snug">{desc}</div>
      <div className="mt-2 text-[10px] font-bold text-amber-700">{busy === key ? "SEEDING…" : "SEED →"}</div>
    </button>
  );
}

const GRADE_TONE = {
  A: "bg-emerald-500 text-white",
  B: "bg-sky-500 text-white",
  C: "bg-amber-500 text-white",
  D: "bg-orange-500 text-white",
  F: "bg-red-600 text-white",
};

function TraineeScorecard() {
  const [scores, setScores] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/training/score");
      setScores(data);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, []);
  React.useEffect(() => {
    refresh();
    const t = setInterval(refresh, 20000);
    return () => clearInterval(t);
  }, [refresh]);
  if (loading && !scores) return null;
  const trainees = scores?.trainees || [];
  return (
    <div data-testid="trainee-scorecard">
      <div className="flex items-center gap-2 mb-2">
        <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-600">Trainee scorecard</div>
        <button onClick={refresh} data-testid="scorecard-refresh"
          className="ml-auto text-[10px] font-semibold text-neutral-500 hover:text-neutral-900">refresh</button>
      </div>
      {trainees.length === 0 ? (
        <p className="text-xs text-neutral-500">No trainees active yet. Provision accounts below to start grading.</p>
      ) : (
        <div className="space-y-2">
          {trainees.map((t) => (
            <div key={t.email} className="rounded-md border border-neutral-200 bg-neutral-50 p-3" data-testid={`scorecard-${t.email}`}>
              <div className="flex items-center gap-3">
                <div className={`w-11 h-11 rounded-lg grid place-items-center font-display font-black text-xl ${GRADE_TONE[t.grade]}`}>{t.grade}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-black text-sm text-neutral-900">{t.name}</span>
                    <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-neutral-900 text-white">{(t.role || "").replace("_", " ")}</span>
                  </div>
                  <div className="text-[11px] text-neutral-500 mt-0.5">
                    {t.activity.loads_created} loads created · {t.activity.cons_sent} sent · {t.activity.cons_countersigned} countersigned
                    {t.activity.prod_cancels > 0 && <span className="text-red-700 font-bold ml-1"> · {t.activity.prod_cancels} PROD cancels</span>}
                    {t.activity.prod_edits > 0 && <span className="text-amber-700 font-bold ml-1"> · {t.activity.prod_edits} PROD edits</span>}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-lg font-bold text-neutral-900">{t.score}</div>
                  <div className="text-[9px] uppercase font-bold text-neutral-500">score</div>
                </div>
              </div>
              {t.flags?.length > 0 && (
                <div className="mt-2 pl-14 space-y-1">
                  {t.flags.map((f, i) => (
                    <div key={i} className={`text-[11px] rounded px-2 py-1 border font-medium ${
                      f.severity === "high" ? "bg-red-50 text-red-800 border-red-200" :
                      f.severity === "med"  ? "bg-amber-50 text-amber-800 border-amber-200" :
                                              "bg-neutral-100 text-neutral-600 border-neutral-200"
                    }`}>
                      {f.text}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ScenariosBlock({ busy, setBusy, onChange }) {
  const [scenarios, setScenarios] = React.useState({ builtins: [], customs: [] });
  const [name, setName] = React.useState("");
  const [desc, setDesc] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get("/admin/training/scenarios");
      setScenarios(data);
    } catch { /* silent */ }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const seedBuiltin = async (code) => {
    setBusy(`seed-${code}`);
    try {
      const { data } = await api.post(`/admin/training/seed-scenario`, null, { params: { scenario: code } });
      toast.success(`Seeded ${data.count} load${data.count === 1 ? "" : "s"}`);
      onChange && onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(""); }
  };
  const seedCustom = async (id) => {
    setBusy(`seed-custom-${id}`);
    try {
      const { data } = await api.post(`/admin/training/scenarios/${id}/seed`);
      toast.success(`Replayed “${data.name}” — ${data.seeded} loads`);
      onChange && onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(""); }
  };
  const saveCustom = async () => {
    if (!name.trim()) return toast.error("Give the scenario a name");
    setSaving(true);
    try {
      const { data } = await api.post("/admin/training/scenarios", { name: name.trim(), description: desc.trim() });
      toast.success(`Saved ${data.load_count} loads as custom scenario`);
      setName(""); setDesc(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };
  const removeCustom = async (id) => {
    if (!window.confirm("Delete this custom scenario? Cannot be undone.")) return;
    try {
      await api.delete(`/admin/training/scenarios/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error("Delete failed"); }
  };
  return (
    <div data-testid="scenarios-block">
      <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-600 mb-2">Scenario presets</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
        {scenarios.builtins.map((s) => (
          <ScenarioTile key={s.code} title={s.name} desc={s.description} scenario={s.code} busy={busy} onSeed={() => seedBuiltin(s.code)} />
        ))}
      </div>

      {scenarios.customs.length > 0 && (
        <>
          <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-600 mb-2 mt-4">Your custom scenarios</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
            {scenarios.customs.map((c) => (
              <div key={c.id} className="rounded-lg border border-sky-200 bg-sky-50 p-3" data-testid={`custom-scenario-${c.id}`}>
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-black text-sm text-neutral-900 truncate">{c.name}</div>
                    <div className="text-[11px] text-neutral-500 mt-0.5">
                      {c.load_count} load{c.load_count === 1 ? "" : "s"} · {c.created_by} · {c.created_at ? fmtDate(c.created_at) : ""}
                    </div>
                    {c.description && <div className="text-[11px] text-neutral-600 mt-1">{c.description}</div>}
                  </div>
                  <button onClick={() => removeCustom(c.id)} className="text-neutral-400 hover:text-red-600 p-1" data-testid={`custom-scenario-delete-${c.id}`}>
                    <X size={12} weight="bold" />
                  </button>
                </div>
                <button onClick={() => seedCustom(c.id)} disabled={busy === `seed-custom-${c.id}`} data-testid={`custom-scenario-seed-${c.id}`}
                  className="mt-2 w-full text-xs font-bold px-3 py-1.5 rounded bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-60">
                  {busy === `seed-custom-${c.id}` ? "Replaying…" : "Replay →"}
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="rounded-lg border border-dashed border-neutral-300 p-3 bg-neutral-50" data-testid="save-scenario-form">
        <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-600 mb-2">Save current training loads as a scenario</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Scenario name (e.g., Onboarding Week 1)"
            data-testid="scenario-name" className="md:col-span-1 px-3 py-2 rounded border border-neutral-200 text-sm focus:outline-none focus:border-neutral-900" />
          <input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Short description (optional)"
            data-testid="scenario-desc" className="md:col-span-1 px-3 py-2 rounded border border-neutral-200 text-sm focus:outline-none focus:border-neutral-900" />
          <button onClick={saveCustom} disabled={saving} data-testid="scenario-save"
            className="md:col-span-1 text-xs font-bold px-3 py-2 rounded bg-neutral-900 text-white hover:bg-black disabled:opacity-60">
            {saving ? "Saving…" : "Snapshot current training loads"}
          </button>
        </div>
        <p className="text-[10px] text-neutral-500 mt-1.5">Captures every training-flagged load in-flight so you can replay the same story for the next new hire.</p>
      </div>
    </div>
  );
}
